// @ts-expect-error
import type {DataProxy} from 'react-apollo';

import {useApolloClient, useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetUsersWithQuotas as GetUsersWithQuotasQuery,
    GetUsersWithQuotasVariables,
    QuotaUpdate as QuotaUpdateMutation,
    QuotaUpdateVariables,
    QuotaUpdateInput,
    GetUsersWithQuotas_usersWithQuotas_edges as UsersEdge,
    GetUsersWithQuotas_usersWithQuotas_edges_node_quotas as Quota,
} from 'nutshell-graphql-types';

import UPDATE_QUOTA from '../mutations/update-quota.graphql';
import GET_USERS_WITH_QUOTAS from '../queries/get-users-with-quotas.graphql';

import {updateYearOverviewStatsCacheFromUpdatingExistingQuota} from './helpers';
import {QUERY_VARIABLES} from './use-get-users-with-quotas';

export function useUpdateQuota(): {
    updateQuota: (input: QuotaUpdateInput) => Promise<any>;
    isLoading: boolean;
    errorMessage: string | null | undefined;
} {
    const client = useApolloClient();
    const [mutate, {loading, error}] = useMutation<QuotaUpdateMutation, QuotaUpdateVariables>(
        UPDATE_QUOTA
    );

    return {
        updateQuota: (input: QuotaUpdateInput) => {
            const previousUsersWithQuotas = client.readQuery<
                GetUsersWithQuotasQuery,
                GetUsersWithQuotasVariables
            >({
                query: GET_USERS_WITH_QUOTAS,
                variables: QUERY_VARIABLES,
            });

            return mutate({
                variables: {input},
                update: (cache) => {
                    updateYearOverviewStatsCacheFromUpdatingExistingQuota(
                        previousUsersWithQuotas,
                        cache,
                        input.userId,
                        input.fiscalYear,
                        input.months
                    );
                    updateUserQuotaInConnection(cache, input);
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        errorMessage: safelyGetGraphQLErrorMessage(error),
    };
}

function updateUserQuotaInConnection(cache: DataProxy, input: QuotaUpdateInput) {
    const existingUsers = cache.readQuery<GetUsersWithQuotasQuery, GetUsersWithQuotasVariables>({
        query: GET_USERS_WITH_QUOTAS,
        variables: QUERY_VARIABLES,
    });

    if (!existingUsers) return;

    const updatedUsers = existingUsers.usersWithQuotas.edges.map((edge: UsersEdge) => {
        if (edge.node.id !== input.userId) return edge;

        const updatedQuotas = edge.node.quotas.map((q: Quota) => {
            if (q.fiscalYear !== input.fiscalYear) return q;

            return {
                ...q,
                months: input.months.map((m) => ({
                    __typename: 'Month' as const,
                    identifier: m.identifier,
                    value: {
                        __typename: 'Money' as const,
                        amount: m.value,
                    },
                })),
            };
        });

        return {...edge, node: {...edge.node, quotas: updatedQuotas}};
    });

    cache.writeQuery<GetUsersWithQuotasQuery, GetUsersWithQuotasVariables>({
        query: GET_USERS_WITH_QUOTAS,
        variables: QUERY_VARIABLES,
        data: {
            ...existingUsers,
            usersWithQuotas: {
                ...existingUsers.usersWithQuotas,
                edges: updatedUsers,
            },
        },
    });
}
